import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Upload, Search, AlertTriangle, 
  UserCheck, UserMinus, Download, Loader2, Plus, History
} from 'lucide-react';
import { adminAPI } from '../../services/api';

const AdminEmployeeReferralsPage = () => {
  const navigate = useNavigate();
  
  // State
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ total_employees: 0, active_codes: 0, pending_collisions: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  
  // Add Employee Form
  const [addName, setAddName] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  
  // CSV Upload
  const [csvFile, setCsvFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Dialogs
  const [collisionDialog, setCollisionDialog] = useState({ open: false, user: null });
  const [customCode, setCustomCode] = useState('');
  const [resolveLoading, setResolveLoading] = useState(false);
  
  const [convertDialog, setConvertDialog] = useState({ open: false, user: null });
  const [convertLoading, setConvertLoading] = useState(false);

  // Fetch employees
  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getEmployeeReferrals(searchQuery, page);
      setEmployees(res.data.employees || []);
      setStats(res.data.stats || {});
      setPagination(res.data.pagination || { total: 0, pages: 1 });
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, page]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Add single employee
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!addName.trim() || !addPhone.trim()) {
      toast.error('Please enter name and phone number');
      return;
    }
    
    setAddLoading(true);
    try {
      const res = await adminAPI.addEmployeeReferral({ name: addName.trim(), phone: addPhone.trim() });
      
      if (res.data.collision) {
        toast.warning(`Code collision! "${res.data.generated_code}" already exists. Please resolve.`);
      } else {
        toast.success(`Employee added with code: ${res.data.referral_code}`);
      }
      
      setAddName('');
      setAddPhone('');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    } finally {
      setAddLoading(false);
    }
  };

  // CSV Upload
  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const res = await adminAPI.uploadEmployeeReferralsCsv(formData);
      setUploadResults(res.data);
      toast.success(`Processed ${res.data.total_rows} rows: ${res.data.successful} successful, ${res.data.collisions} collisions`);
      setCsvFile(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process CSV');
    } finally {
      setUploadLoading(false);
    }
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const res = await adminAPI.getEmployeeReferralsCsvTemplate();
      const blob = new Blob([res.data.content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Resolve collision
  const handleResolveCollision = async () => {
    if (!customCode.trim()) {
      toast.error('Please enter a custom code');
      return;
    }
    
    setResolveLoading(true);
    try {
      await adminAPI.resolveEmployeeReferralCollision(collisionDialog.user.user_id, { custom_code: customCode.trim() });
      toast.success(`Code set to "${customCode.trim().toUpperCase()}"`);
      setCollisionDialog({ open: false, user: null });
      setCustomCode('');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve collision');
    } finally {
      setResolveLoading(false);
    }
  };

  // Convert to customer
  const handleConvertToCustomer = async () => {
    setConvertLoading(true);
    try {
      const res = await adminAPI.convertEmployeeToCustomer(convertDialog.user.user_id);
      toast.success(`Converted to customer. New code: ${res.data.new_code}`);
      setConvertDialog({ open: false, user: null });
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    } finally {
      setConvertLoading(false);
    }
  };

  // Search with debounce
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8" data-testid="admin-employee-referrals-page">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-semibold">Employee Referral Codes</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-2xl font-bold">{stats.total_employees}</p>
              <p className="text-sm text-gray-500">Total Employees</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <UserCheck className="w-6 h-6 mx-auto mb-2 text-green-600" />
              <p className="text-2xl font-bold">{stats.active_codes}</p>
              <p className="text-sm text-gray-500">Active Codes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-orange-600" />
              <p className="text-2xl font-bold">{stats.pending_collisions}</p>
              <p className="text-sm text-gray-500">Pending Collisions</p>
            </CardContent>
          </Card>
        </div>

        {/* Add Employee & CSV Upload */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Add Single Employee */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Plus className="w-5 h-5" /> Add Employee
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddEmployee} className="space-y-4">
                <div>
                  <Label htmlFor="emp-name">Full Name</Label>
                  <Input
                    id="emp-name"
                    placeholder="e.g., Rahul Kumar"
                    value={addName}
                    onChange={(e) => setAddName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="emp-phone">Phone Number</Label>
                  <Input
                    id="emp-phone"
                    placeholder="e.g., 9160373366"
                    value={addPhone}
                    onChange={(e) => setAddPhone(e.target.value)}
                  />
                </div>
                <Button type="submit" disabled={addLoading} className="w-full">
                  {addLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Generate Code
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* CSV Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="w-5 h-5" /> Bulk Upload
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div 
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50"
                onClick={() => document.getElementById('csv-input').click()}
              >
                <input
                  id="csv-input"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                />
                {csvFile ? (
                  <p className="text-green-600 font-medium">{csvFile.name}</p>
                ) : (
                  <p className="text-gray-500">Click to select CSV file</p>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDownloadTemplate}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" /> Template
                </Button>
                <Button 
                  onClick={handleCsvUpload} 
                  disabled={!csvFile || uploadLoading}
                  className="flex-1"
                >
                  {uploadLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Upload
                </Button>
              </div>

              {/* Upload Results */}
              {uploadResults && (
                <div className="text-sm p-3 bg-gray-100 rounded">
                  <p>✓ Successful: {uploadResults.successful}</p>
                  <p>⚠ Collisions: {uploadResults.collisions}</p>
                  <p>✗ Failed: {uploadResults.failed}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Employee List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Employee List</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search name, phone, code..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : employees.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No employees found. Add employees using the form above.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm text-gray-500">
                        <th className="pb-3 font-medium">Name</th>
                        <th className="pb-3 font-medium">Phone</th>
                        <th className="pb-3 font-medium">Code</th>
                        <th className="pb-3 font-medium">Referrals</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((emp) => (
                        <tr key={emp.user_id} className="border-b last:border-0">
                          <td className="py-3 font-medium">{emp.name}</td>
                          <td className="py-3 text-gray-600">{emp.phone}</td>
                          <td className="py-3">
                            {emp.status === 'collision' ? (
                              <span className="text-orange-600 font-mono">—</span>
                            ) : (
                              <span className="font-mono bg-gray-100 px-2 py-1 rounded">{emp.referral_code}</span>
                            )}
                          </td>
                          <td className="py-3">{emp.total_referrals || 0}</td>
                          <td className="py-3">
                            {emp.status === 'collision' ? (
                              <Badge variant="outline" className="text-orange-600 border-orange-300">
                                <AlertTriangle className="w-3 h-3 mr-1" /> Collision
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-green-600 border-green-300">
                                Active
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 text-right space-x-2">
                            {emp.status === 'collision' ? (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setCollisionDialog({ open: true, user: emp });
                                  setCustomCode(emp.pending_code || '');
                                }}
                              >
                                Resolve
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => setConvertDialog({ open: true, user: emp })}
                              >
                                <UserMinus className="w-4 h-4 mr-1" /> Convert
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <span className="px-3 py-1 text-sm text-gray-600">
                      Page {page} of {pagination.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === pagination.pages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resolve Collision Dialog */}
      <Dialog open={collisionDialog.open} onOpenChange={(open) => setCollisionDialog({ open, user: collisionDialog.user })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Code Collision</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              The generated code <span className="font-mono font-bold">{collisionDialog.user?.pending_code}</span> already exists.
              Please enter a custom code for <span className="font-bold">{collisionDialog.user?.name}</span>.
            </p>
            <div>
              <Label>Custom Code</Label>
              <Input
                placeholder="e.g., RAHU3366A"
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                className="font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">Suggestion: Add a letter suffix like "A" or "B"</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCollisionDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button onClick={handleResolveCollision} disabled={resolveLoading}>
              {resolveLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Set Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Customer Dialog */}
      <Dialog open={convertDialog.open} onOpenChange={(open) => setConvertDialog({ open, user: convertDialog.user })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to Customer</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to convert <span className="font-bold">{convertDialog.user?.name}</span> from employee to customer?
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Their referral code <span className="font-mono font-bold">{convertDialog.user?.referral_code}</span> will be replaced with a new standard customer code.
              All existing referrals will be transferred.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConvertToCustomer} disabled={convertLoading}>
              {convertLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Convert to Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmployeeReferralsPage;
